import React, { useEffect, useState } from "react";
import { apiRequestWithAuth } from "../../utils/api";
import Alert from "../../components/ui/alert/Alert";
import PageMeta from "../../components/common/PageMeta";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import ComponentCard from "../../components/common/ComponentCard";
import {useNavigate, useParams} from "react-router-dom";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../../components/ui/table";
import Label from "../../components/form/Label";

const EditOrderPage = () => {
    const { id } = useParams<{ id: string }>();
    const defaultForm = {
        id: "",
        userId: "",
        user: {
            id: "",
            name: "",
            email: "",
            phoneNumber: ""
        },
        totalPrice: 0,
        totalDiscount: 0,
        vat: 0,
        totalPaySum: 0,
        status: 1,
        statusText: "",
        createTime: "",
    };

    interface item {
        id: "",
        orderId: "",
        courseId: 0,
        courseName: "",
        salePrice: 0,
        regularPrice: 0,
        quantity: 1,
        totalPrice: 0
    }

    const [formData, setFormData] = useState(defaultForm);
    const [items, setItems] = useState<item[]>([]);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const navigate = useNavigate();

    // Fetch existing course
    useEffect(() => {
        const fetchPost = async () => {
            try {
                const res = await apiRequestWithAuth(`/orders/${id}`, "GET", {});
                const data = res.result;

                setFormData(
                    {
                        id: data.id,
                        userId: data.userId,
                        user: {
                            id: data.user?.id || "",
                            name: data.user?.name || "",
                            email: data.user?.email || "",
                            phoneNumber: data.user?.phoneNumber || "N/A"
                        },
                        totalPrice: data.totalPrice || 0,
                        totalDiscount: data.totalDiscount || 0,
                        vat: data.vat || 0,
                        totalPaySum: data.totalPaySum || 0,
                        status: data.status || 1,
                        statusText: data.statusText || "",
                        createTime: data.createTime || "",
                    }
                );

                setItems(data.items);


            } catch (error) {
                console.error("Lỗi khi lấy dữ liệu slider:", error);
            }
        };

        if (id) fetchPost();
    }, [id]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const name = "metaDescription";
        const value = e.target ? e.target.value : "";
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const name = "shortDescription";
        const value = e.target ? e.target.value : "";
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSave = async () => {
        try {
            await apiRequestWithAuth(`/orders/${id}`, "PUT", formData);
            setOpenSnackbar(true);
            setFormData(defaultForm);
            setTimeout(() => navigate("/orders"), 1000);
        } catch (error) {
            console.error("Update post failed", error);
        }
    };

    const formatVND = (amount: number | bigint) => {
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
        }).format(amount);
    };

    return (
        <div>
            <PageMeta title="Chi tiết đơn hàng" description="Trang chi tiết đơn hàng" />
            <PageBreadcrumb pageTitle="Chi tiết đơn hàng" />

            {openSnackbar && (
                <Alert
                    variant="success"
                    title="Cập nhật đơn hàng thành công"
                    message="Bạn đã tạo đơn hàng thành công."
                />
            )}

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-1">
                <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03] ">
                    <div className="px-6 py-5">
                        <div
                            className="flex items-center justify-between rounded-2xl bg-white dark:bg-white/[0.03]">
                            <div className="flex items-center gap-4">
                                <div
                                    className="flex h-[52px] w-[52px] items-center justify-center rounded-xl bg-orange-500/[0.08] text-orange-500">
                                    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none"
                                         xmlns="http://www.w3.org/2000/svg" className="size-6">
                                        <path fillRule="evenodd" clipRule="evenodd"
                                              d="M5.5 3.25C4.25736 3.25 3.25 4.25736 3.25 5.5V8.99998C3.25 10.2426 4.25736 11.25 5.5 11.25H9C10.2426 11.25 11.25 10.2426 11.25 8.99998V5.5C11.25 4.25736 10.2426 3.25 9 3.25H5.5ZM4.75 5.5C4.75 5.08579 5.08579 4.75 5.5 4.75H9C9.41421 4.75 9.75 5.08579 9.75 5.5V8.99998C9.75 9.41419 9.41421 9.74998 9 9.74998H5.5C5.08579 9.74998 4.75 9.41419 4.75 8.99998V5.5ZM5.5 12.75C4.25736 12.75 3.25 13.7574 3.25 15V18.5C3.25 19.7426 4.25736 20.75 5.5 20.75H9C10.2426 20.75 11.25 19.7427 11.25 18.5V15C11.25 13.7574 10.2426 12.75 9 12.75H5.5ZM4.75 15C4.75 14.5858 5.08579 14.25 5.5 14.25H9C9.41421 14.25 9.75 14.5858 9.75 15V18.5C9.75 18.9142 9.41421 19.25 9 19.25H5.5C5.08579 19.25 4.75 18.9142 4.75 18.5V15ZM12.75 5.5C12.75 4.25736 13.7574 3.25 15 3.25H18.5C19.7426 3.25 20.75 4.25736 20.75 5.5V8.99998C20.75 10.2426 19.7426 11.25 18.5 11.25H15C13.7574 11.25 12.75 10.2426 12.75 8.99998V5.5ZM15 4.75C14.5858 4.75 14.25 5.08579 14.25 5.5V8.99998C14.25 9.41419 14.5858 9.74998 15 9.74998H18.5C18.9142 9.74998 19.25 9.41419 19.25 8.99998V5.5C19.25 5.08579 18.9142 4.75 18.5 4.75H15ZM15 12.75C13.7574 12.75 12.75 13.7574 12.75 15V18.5C12.75 19.7426 13.7574 20.75 15 20.75H18.5C19.7426 20.75 20.75 19.7427 20.75 18.5V15C20.75 13.7574 19.7426 12.75 18.5 12.75H15ZM14.25 15C14.25 14.5858 14.5858 14.25 15 14.25H18.5C18.9142 14.25 19.25 14.5858 19.25 15V18.5C19.25 18.9142 18.9142 19.25 18.5 19.25H15C14.5858 19.25 14.25 18.9142 14.25 18.5V15Z"
                                              fill="currentColor"></path>
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="mb-1 text-sm font-medium text-gray-800 dark:text-white/90">Thông tin đơn hàng</h4>
                                    <div className="block text-sm text-gray-500 dark:text-gray-400">
                                        <span className="mr-2">Trạng thái: </span>
                                        <strong
                                            className={`inline-flex items-center px-2.5 py-0.5 justify-center gap-1 rounded-full font-medium text-theme-xs 
                                            ${formData.statusText == 'Success' ? 'bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-600' : 'bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-600'}`}>
                                          {formData.statusText}
                                        </strong>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <span className="block mb-1 text-sm text-right text-gray-500 dark:text-gray-400">Mã đơn hàng</span>
                                <strong className="block text-sm text-right text-black-500 dark:text-black-400">ID: #{formData.id}</strong>
                            </div>
                        </div>
                    </div>
                    <div className="p-4 border-t border-gray-100 dark:border-gray-800 sm:p-6">
                        <div className="flex flex-col gap-6 mb-9 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <span className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-400">Khách hàng</span>
                                <h5 className="mb-4 text-base font-semibold text-gray-800 dark:text-white/90">{formData.user.name}</h5>
                                <p className="mb-4 text-sm text-gray-500 dark:text-gray-400"><span>SĐT:</span> {formData.user.phoneNumber}, <br/>Email: {formData.user.email}</p>
                                <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">Ngày đặt hàng:</span>
                                <span className="block text-sm text-gray-500 dark:text-gray-400">{formData.createTime}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="space-y-6">
                    <ComponentCard title="Thông tin thanh toán" desc={`Mã thanh toán: #${formData.id}`}>
                        <div className="space-y-6">

                        </div>
                    </ComponentCard>
                    <ComponentCard title="Items" desc="Thông tin khóa học đã mua">
                        <Table>
                            {/* Table Header */}
                            <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                                <TableRow>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400 text-nowrap"
                                    >
                                        Course ID
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400 text-nowrap"
                                    >
                                        Khóa học
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-end text-theme-xs dark:text-gray-400 text-nowrap"
                                    >
                                        Giá bán
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-center text-theme-xs dark:text-gray-400 text-nowrap"
                                    >
                                        Số lượng
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-end text-theme-xs dark:text-gray-400 text-nowrap"
                                    >
                                        Tổng tiền
                                    </TableCell>
                                </TableRow>
                            </TableHeader>
                            {/* Table Body */}
                            <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                                {items.map((item) => (
                                    <TableRow key={item.courseId}>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                            {item.courseId}
                                        </TableCell>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                            {item.courseName}
                                        </TableCell>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-end text-theme-sm dark:text-gray-400">
                                            {formatVND(item.salePrice ? item.salePrice : item.regularPrice)}
                                        </TableCell>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-center text-theme-sm dark:text-gray-400">
                                            {item.quantity}
                                        </TableCell>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-end text-theme-sm dark:text-gray-400">
                                            {formatVND(item.totalPrice)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell>
                                    </TableCell>
                                    <TableCell>
                                    </TableCell>
                                    <TableCell>
                                    </TableCell>
                                    <TableCell className="text-end px-4 py-3 text-theme-sm dark:text-gray-400">
                                        <span className="font-medium text-gray-700 dark:text-gray-400">Tổng tiền:</span>
                                    </TableCell>
                                    <TableCell className="text-end px-4 py-3 text-theme-sm dark:text-gray-400">
                                        <span className="font-medium text-gray-800 dark:text-white/90">
                                            {formatVND(formData.totalPaySum)}
                                        </span>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                    </TableCell>
                                    <TableCell>
                                    </TableCell>
                                    <TableCell>
                                    </TableCell>
                                    <TableCell className="text-end px-4 py-3 text-theme-sm dark:text-gray-400">
                                        <span className="font-medium text-gray-700 dark:text-gray-400">VAT:</span>
                                    </TableCell>
                                    <TableCell className="text-end px-4 py-3 text-theme-sm dark:text-gray-400">
                                        <span className="font-medium text-gray-800 dark:text-white/90">
                                            {formatVND(formData.vat)}
                                        </span>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                    </TableCell>
                                    <TableCell>
                                    </TableCell>
                                    <TableCell>
                                    </TableCell>
                                    <TableCell className="text-end px-4 py-3 text-theme-sm dark:text-gray-400">
                                        <span className="font-medium text-gray-700 dark:text-gray-400">Giảm giá:</span>
                                    </TableCell>
                                    <TableCell className="text-end px-4 py-3 text-theme-sm dark:text-gray-400">
                                        <span className="font-medium text-gray-800 dark:text-white/90">
                                            {formatVND(formData.totalDiscount)}
                                        </span>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                    </TableCell>
                                    <TableCell>
                                    </TableCell>
                                    <TableCell>
                                    </TableCell>
                                    <TableCell className="text-end px-4 py-3 text-theme-sm dark:text-gray-400">
                                        <span
                                            className="font-medium text-gray-700 dark:text-gray-400">Thành tiền:</span>
                                    </TableCell>
                                    <TableCell className="text-end px-4 py-3 text-theme-sm dark:text-gray-400">
                                        <span className="font-medium text-gray-800 dark:text-white/90">
                                            {formatVND(formData.totalPaySum)}
                                        </span>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                        <div>
                            <Label>Trạng thái</Label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                className="block w-full border border-gray-300 rounded-md shadow-sm p-2"
                            >
                                <option value="1">Pending</option>
                                <option value="2">Success</option>
                                <option value="3">Waiting Payment</option>
                                <option value="4">Cancelled</option>
                                <option value="5">Refunded</option>
                            </select>
                        </div>
                    </ComponentCard>
                </div>

                <button
                    onClick={handleSave}
                    className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300"
                >
                    Câp nhật đơn hàng
                </button>
            </div>
        </div>
    );
};

export default EditOrderPage;
